import React, {useState, useEffect, useRef}		from 'react';
import SvgColor									from 'react-svg-color';

import Load										from '../Load/Load';
import Icon										from '../Icon';

import {ButtonProps}							from './interface';
import {ChildrenElement}						from '../../typescript-declaration/global';

import * as ft_divPosition						from '../../functions/divPosition';
import ft_cssVar								from '../../functions/getCssVar';

import img_chevronDown							from '../../images/icon/chevron-down.svg';

import './Button.css';


const defaultProps: ButtonProps = {
	children:   '',
	onClick:    () => null,
	type:       'primary',
	size:       'md',
	disabled:   false,
	iconOnly:   false,
	fullWidth:  false
};

function Button(props: ButtonProps) {
	props = {...defaultProps, ...props};

	const [open, set_open] = useState(false);
	const [anim, set_anim] = useState(false);
	const [isFocused, set_isFocused] = useState(false);
	const [absolutePosition, set_absolutePosition] = useState<React.CSSProperties>({top: 0, left: 0, minWidth: 0, maxWidth: 0});

	const buttonEl = useRef<HTMLDivElement>(null);

	const style = props.style || {};
	useEffect(() => {
		function handleScroll() {
			if (open && buttonEl.current) {
				const position = ft_divPosition.getWindowPosition(buttonEl.current);

				let maxWidth = (buttonEl.current.offsetWidth - 2) * 4;

				if (!props.absoluteRight && maxWidth + position.left > window.innerWidth - 10)
					maxWidth = window.innerWidth - 10 - position.left;

				set_absolutePosition({
					maxWidth: !props.absoluteRight ? maxWidth : 'unset',
					left:     props.absoluteRight ? 'unset' : position.left,
					right:    !props.absoluteRight ? 'unset' : window.innerWidth - position.right,
					minWidth: buttonEl.current.offsetWidth - 2,
					top:      (position.top + buttonEl?.current?.offsetHeight) + 3
					// right:    !props.absoluteRight ? 'unset' : buttonEl.current?.offsetWidth,
				});
			}
		}

		if (!open)
			set_absolutePosition({top: 0, left: 0, minWidth: 0, maxWidth: 0});
		else if (!anim)
			set_anim(true);

		handleScroll();

		const parentElements: HTMLElement[] = [];
		let 	currentElement: HTMLElement | null = buttonEl.current;

		while (currentElement) {
			const parent = currentElement.parentElement;
			if (parent) {
				parentElements.push(parent);
				currentElement = parent;
			} else
				currentElement = null;
		}

		parentElements.forEach(element => element.addEventListener('scroll', handleScroll));

		return () => parentElements.forEach(element => element.removeEventListener('scroll', handleScroll));
	}, [open]);

	function renderIcon(icon: string) {
		if (!icon.endsWith('.svg'))
			return (<img className={'buttonIcon'} src={icon} alt={'icon'} />);
		return (
			<div className={'buttonIconContainer'}>
				<SvgColor
					svg={icon}
					colors={[iconColor]}
				/>
			</div>
		);
	}

	function renderAbsolute() {
		const render: React.ReactElement[] = [];

		props.absolute?.forEach((e, i) => {
			const onClickItem = () => {
				if (props.onChangeAbsolute)
					props.onChangeAbsolute(e.value);
				set_open(false);
			};

			render.push(
				<div
					key={`select-${i}`}
					className={`selectItem ${props.absoluteItemClassname} ${e.disabled && 'disabled'}`}
					onClick={() => (!e.disabled) && onClickItem()}
				>
					<div className={'flex'}>{e.render}</div>
				</div>
			);
		});

		return (
			<div
				className={'selectAbsolute'}
				onClick={e => e.stopPropagation()}
				style={absolutePosition}
			>
				<div className={'selectAbsoluteContent'}>
					{render}
				</div>
			</div>
		);
	}

	const onClick = (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (props.isLoad || props.disabled)
			return;

		if (props.absolute)
			set_open(!open);
		if (props.onClick)
			props.onClick(event);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' && isFocused)
			onClick();
	};

	let render: ChildrenElement = props.children;
	let iconColor = '#ffffff';

	if (props.valueAbsolute) {
		const foundData = props.absolute?.find(find => find?.value === props.valueAbsolute);

		if (foundData?.value)
			render = <span>{foundData.render}</span>;
	}

	if (props.type === 'secondary-gray')
		iconColor = (props.disabled) ? '#D0D5DD' : '#344054';
	if (props.type === 'secondary')
		iconColor = (props.disabled) ? '#BDB4FE' : '#5925DC';
	if (props.type === 'tertiary')
		iconColor = (props.disabled) ? '#D0D5DD' : '#5925DC';
	if (props.type === 'tertiary-gray')
		iconColor = (props.disabled) ? ' #D0D5DD' : '#475467';
	if (props.type === 'danger-reverse')
		iconColor = (props.disabled) ? ' #FDA29B' : '#B42318';

	if (props.color) {
		iconColor = style?.color?.toString() || iconColor;
		style.color = props.color;
		style.borderColor = props.color;
	}

	if (props.targetLink && !props.disabled) {
		return (
			<a
				href={props.targetLink}
				target={'_blank'}
				rel={'noreferrer'}
				className={`button ${props.size} ${props.type} ${props.iconOnly && 'iconOnly'} ${props.fullWidth && 'fullWidth'}`}
				onClick={() => (!props.disabled && props.onClick) && props.onClick()}
				style={style}
			>
				<div className={`buttonCircle ${props.size} ${props.type}`} />
				{!props.iconOnly && <span>{props.children}</span>}
				{!props.iconOnly && <div className={`buttonCircle ${props.size} ${props.type} ${props.disabled && 'disabled'}`} />}
			</a>
		);
	}

	return (
		<div
			tabIndex={0}
			style={style}
			ref={buttonEl}
			role={'button'}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			onFocus={() => set_isFocused(true)}
			onBlur={() => {
				set_isFocused(false);
				set_open(false);
			}}
			className={`button ${props.size} ${props.type} ${props.disabled && 'disabled'} ${props.iconOnly && 'iconOnly'} ${props.fullWidth && 'fullWidth'} ${props.isLoad && 'isLoad'} ${props.className} text-${props.size} ${props.noFocusCss && 'noFocus'}`}
		>
			{props.iconBefore && renderIcon(props.iconBefore)}

			{!props.iconOnly && render}

			{props.icon && renderIcon(props.icon)}

			{(props.absolute && !props.absoluteHideIcon) &&
				<div className={`selectChevron buttonChevron ${open ? 'open' : 'close'} ${anim && 'anim'}`}>
					<Icon
						iconSize={10}
						icon={img_chevronDown}
						iconColor={props.type ? ft_cssVar(`${props.type}-700`) : undefined}
					/>
				</div>
			}

			{props.isLoad && <Load size={14} />}

			{(open && absolutePosition.top !== 0) && renderAbsolute()}
		</div>
	);
}

export default Button;